import { DecimalPipe, NgClass, NgTemplateOutlet } from "@angular/common";
import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { RouterLink } from "@angular/router";
import { ArbetsorderInfoIconComponent } from "@components/arbetsorder-info-icon/arbetsorder-info-icon.component";
import { IconComponent } from "@components/icon/icon.component";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective } from "@ngx-translate/core";
import { DayPilot } from "daypilot-pro-angular";
import { BehaviorSubject } from "rxjs";
import { IMessageLabel, IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { Arbetsordertyper, Operationstyper, Produktionsstatus, VirkeskopAffarsformer } from "~app/common/models/constants";
import { IArbetsorderAtgarder } from "~app/features/planera/model/IArbetsorderAtgarder";
import {
    BegransaVirkesorderraderModalComponent
} from "~app/features/transport/transportplanera/components/tpl-omdestinera/components/begransa-virkesorderrader-modal/begransa-virkesorderrader-modal.component";
import { ArbetsorderHeaderListRowComponent } from "../arbetsorder-header-list-row/arbetsorder-header-list-row.component";
import { ListEvents } from "../events/list-events";
import { Origin } from "../events/origin";
import { PlaneraEvents } from "../events/planera-events";
import { PlaneraKartaEventService } from "../planera-karta/planera-karta-event.service";
import { ArbetsorderService } from "../services/arbetsorder.service";

@Component({
    selector: "rask-arbetsorder-header",
    styleUrls: ["./arbetsorder-header.component.scss"],
    templateUrl: "./arbetsorder-header.component.html",
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        ArbetsorderInfoIconComponent,
        RouterLink,
        NgTemplateOutlet,
        ArbetsorderHeaderListRowComponent,
        TranslateDirective,
        IconComponent,
        DecimalPipe
    ]
})
export class ArbetsorderHeaderComponent implements OnChanges {
    @Input() arbetsorder: IArbetsorderAtgarder;
    @ViewChild("draggableArbetsorder") draggableArbetsorder: ElementRef;
    @Input() isDetailedView: any;
    messageItems: IMessageModel[] = [
        {
            severity: MessageSeverity.Info,
            message: "Antal rader på arbertsordern har justerats.",
            anchor: "#click-me-i-am-anhor"
        }
    ];
    messageItemsAsObservable: BehaviorSubject<IMessageModel[]>;
    messageLabels: IMessageLabel[] = [
        {
            severity: MessageSeverity.Critical,
            labelText: "Hantera arbetsorderns rader"
        }
    ];

    private readonly arbetsorderService = inject(ArbetsorderService);
    private readonly modalService = inject(NgbModal);
    private readonly planeraKartaEventService = inject(PlaneraKartaEventService);

    constructor() {
        this.messageItemsAsObservable = new BehaviorSubject<IMessageModel[]>([]);
    }

    get canDrag(): boolean {
        return (
            this.arbetsorder.affarsform !== VirkeskopAffarsformer.LeveransvirkeForShort &&
            !this.arbetsorderService.isSchemalagd(this.arbetsorder.periodsattPeriod, this.arbetsorder.operationer) &&
            this.arbetsorder.produktionsstatus !== Produktionsstatus.Makulerad
        );
    }

    get dragInfo() {
        return {
            id: this.arbetsorder.id,
            beteckning: this.arbetsorder.beteckning,
            namn: this.arbetsorder.namn,
            externalHtml: this.getExternalHtml(this.arbetsorder.namn),
            duration: 86400,
            element: null,
            affarsform: this.arbetsorder.affarsform,
            anskaffningsform: this.arbetsorder.anskaffningsform,
            avverkningFarStartaDatum: this.arbetsorder.avverkningFarStartaDatum,
            operationstyp: this.arbetsorder.isGrotskotning ? Operationstyper.Grotskotning : Operationstyper.Skordning,
            onDragStart: this.getOperationInfoForScheduler
        };
    }

    get hasKvalitetssakradVolym(): boolean {
        if (this.arbetsorder.typ !== Arbetsordertyper.Grotskotning) {
            return true;
        }

        return this.arbetsorder.atgarder.every(x => x.kvalitetssakradGrot);
    }

    getOperationInfoForScheduler = async (args) => {
        if (!this.canDrag) {
            await this.stopDrag(); // Workaround för att stoppa drag&drop i AO Detaljvy
        } else {
            const info = await this.arbetsorderService.getOperationInfoForScheduler(this.arbetsorder.id);
            args.data.id = info.arbetsorderId;
            args.data.beteckning = info.arbetsorderBeteckning;
            args.data.namn = info.arbetsordernamn;
            args.data.externalHtml = this.getExternalHtml(info.arbetsordernamn);
            args.data.affarsform = info.arbetsorderAffarsform;
            args.data.anskaffningsform = info.arbetsorderAnskaffningsform;
            args.data.avverkningFarStartaDatum = info.avverkningFarStartaDatum;
            args.data.avverkningGiltigtTillDatum = info.avverkningGiltigtTillDatum;
            args.data.certifikatSlutDatum = info.certifikatSlutDatum;
            args.data.certifikatStartDatum = info.certifikatStartDatum;
            args.data.arbetsorderRowVersion = info.arbetsorderRowVerion;
            args.data.skordningPaborjadDatum = info.skordningPaborjadDatum;
            args.data.skotningAvslutadDatum = info.skotningAvslutadDatum;
            args.data.volymArbetsorder = info.volymArbetsorder;
        }
    };

    hoverRow(beteckning: string, mouseover: boolean) {
        if (!this.isDetailedView) {
            this.planeraKartaEventService.emit(
                PlaneraEvents.mouseOverArbetsorderChanged,
                {
                    origin: Origin.listHeader,
                    beteckning: beteckning,
                    isMouseover: mouseover
                }
            );
            if (this.canDrag && mouseover) {
                const dragInfo = this.dragInfo;
                dragInfo.element = this.draggableArbetsorder.nativeElement;
                DayPilot.Scheduler.makeDraggable(dragInfo);
            }
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.arbetsorder && changes.arbetsorder.currentValue !== undefined) {
            this.messageItemsAsObservable.next(this.messageItems);

            const ao = changes.arbetsorder;
            this.arbetsorder = ao.currentValue;
        }
    }

    openHanteraRaderModal() {
        BegransaVirkesorderraderModalComponent.open(this.arbetsorder.id, "standard", this.modalService);
    }

    selectRow(beteckning: string) {
        this.arbetsorder.selected =
            this.arbetsorder.selected === undefined || this.arbetsorder.selected === false
                ? true
                : false;
        this.planeraKartaEventService.emit(
            ListEvents.aoHeaderSelect,
            {
                origin: Origin.listHeader,
                beteckning: beteckning,
                selected: this.arbetsorder.selected
            }
        );
    }

    private readonly getExternalHtml = (namn: string) =>
        `<div class="ps-3" style="min-width: 180px; background-color: black; color: white;">${namn}</div>`;

    private readonly stopDrag = () => new Promise(() => setTimeout(() => DayPilot.Scheduler.stopDragging(), 1));
}
